import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Skeleton from '@mui/material/Skeleton';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';

const useStyles = makeStyles(() => ({
    container: {
        boxShadow: '0px 4.8px 14.4px rgb(0 0 0 / 18%)',
        borderRadius: '4px',
        minHeight: '300px',
    },
    skeleton: {
        borderRadius: '4px',
        margin: '5px'
    },
    emptyCell: {
        width: '100%',
        textAlign: 'center',
        verticalAlign: 'middle',
        height: '200px',
        fontSize: '14px',
        color: '#8C8C8B',
    },
    emptyCellTitle: {
        width: '100%',
        textAlign: 'center',
        verticalAlign: 'middle',
        fontWeight: '600',
        fontSize: '16px',
        color: '#000',
    },
    emptyHeaderCell: {
        width: '100%',
        textAlign: 'center',
        verticalAlign: 'middle',
        fontWeight: '600',
        fontSize: '16px',
        color: '#000',
        padding: '16px !important',
        borderBottom: '2px solid rgba(0,0,0,0.12) !important',
        backgroundColor: '#fafafa !important',
    }
}));

const useRowStyles = makeStyles({
    root: {
        '& > *': {
            borderBottom: 'unset',
        },
    },
    evenRow: {
        backgroundColor: '#e9e9e9'
    },
    wideHeader: {
        paddingLeft: '12rem'
    }
});

function SecondDimensionRow(props) {
    const [expandedSecondDimension, setExpandedSecondDimension] = useState(false);
    const { row, thirdDimensionDataPosition, columns, retrieveDataOnSecondDimension,
        secondDimensionData, secondDimensionDataPosition, thirdDimensionColumns, dataIsLoading } = props;
    const classes = useRowStyles();
    const [currentPage, setCurrentPage] = useState(1);
    const thirdDimensionData = 
        secondDimensionData.childrenDimensionData !== undefined ?
        secondDimensionData.childrenDimensionData[thirdDimensionDataPosition] : {};

    const handleClickOnNext = () => {
        if (Object.keys(thirdDimensionData).length) {
            const lastDate = Math.min(...thirdDimensionData.data.map(thirdDimensionRow => Number(Date.parse(thirdDimensionRow.Date))));
            const lastKey = new Date(lastDate).toISOString().slice(0, 19).replace('T', ' ');

            const matchingUIDs = thirdDimensionData.data
                .filter(thirdDimensionRow => Number(Date.parse(thirdDimensionRow.Date)) === lastDate)
                .map(thirdDimensionRow => thirdDimensionRow.RowNumber);

            retrieveDataOnSecondDimension(
                thirdDimensionDataPosition,
                secondDimensionDataPosition,
                row.RuleName,
                row.ClientName,
                row.ProgramName,
                row.ProjectName,
                row.SiteName,
                true,
                matchingUIDs,
                lastKey,
                true
            );

            setCurrentPage(currentPage + 1);
        }
    }

    const handleClickOnPrevious = () => {
        if (Object.keys(thirdDimensionData).length) {
            const lastDate = Math.max(...thirdDimensionData.data.map(thirdDimensionRow => Number(Date.parse(thirdDimensionRow.Date))));
            const lastKey = new Date(lastDate).toISOString().slice(0, 19).replace('T', ' ');

            const matchingUIDs = thirdDimensionData.data
                .filter(thirdDimensionRow => Number(Date.parse(thirdDimensionRow.Date)) === lastDate)
                .map(thirdDimensionRow => thirdDimensionRow.RowNumber);

            retrieveDataOnSecondDimension(
                thirdDimensionDataPosition,
                secondDimensionDataPosition,
                row.RuleName,
                row.ClientName,
                row.ProgramName,
                row.ProjectName,
                row.SiteName,
                true,
                matchingUIDs,
                lastKey,
                false
            );

            setCurrentPage(currentPage - 1);
        }
    }

    const handleExpandSecondDimension = () => {
        retrieveDataOnSecondDimension(
            thirdDimensionDataPosition,
            secondDimensionDataPosition,
            row.RuleName,
            row.ClientName,
            row.ProgramName,
            row.ProjectName,
            row.SiteName,
            !expandedSecondDimension
        );
        setExpandedSecondDimension(!expandedSecondDimension);
    }

    return (
        columns.length && Object.keys(row).length ? <>
            <TableRow className={classes.root}>
                <TableCell>
                    <IconButton disabled={dataIsLoading} aria-label="expand row" size="small" onClick={handleExpandSecondDimension}>
                        {expandedSecondDimension ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                {columns.map((column, index) => {
                            if (index === 0) {
                                return column ? <TableCell component="th" scope="row" key={`${column}${row[column]}`}>{row[column]}</TableCell> : null;
                            }

                            return column ? <TableCell key={`${column}${row[column]}`} align="right">{row[column]}</TableCell> : null;
                        })}
            </TableRow>
            {thirdDimensionColumns?.length ? <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                    <Collapse in={expandedSecondDimension} timeout="auto" unmountOnExit>
                        <Box margin={1}>
                            <Typography variant="h6" gutterBottom component="div">
                                Page {currentPage} of {thirdDimensionData?.count}&nbsp;&nbsp;&nbsp;
                                <IconButton disabled={dataIsLoading || currentPage === 1} onClick={handleClickOnPrevious}><NavigateBeforeIcon  /></IconButton>&nbsp;
                                <IconButton disabled={dataIsLoading || currentPage === thirdDimensionData?.count} onClick={handleClickOnNext}><NavigateNextIcon /></IconButton>
                            </Typography>
                            <Table size="small" aria-label="purchases">
                                <TableHead>
                                    <TableRow>
                                        {thirdDimensionColumns.map((column, index) => {
                                            if (index <= 1) {
                                                return column ? <TableCell key={`${column}`}>{column}</TableCell> : null;
                                            }

                                            if (column === 'Date') {
                                                return column ? <TableCell className={classes.wideHeader} key={`${column}`} align="right">{column}</TableCell> : null;
                                            }

                                            return column ? <TableCell key={`${column}`} align="right">{column}</TableCell> : null;
                                        })}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {
                                        Object.keys(thirdDimensionData ?? {}).length && !dataIsLoading ? thirdDimensionData.data.map((thirdDimensionRow, index) => (
                                            <TableRow className={index % 2 === 0 ? classes.evenRow : ''} key={thirdDimensionRow.RowNumber}>
                                                <TableCell key={thirdDimensionRow.AlertId} component="th" scope="row">{thirdDimensionRow.AlertId}</TableCell>
                                                <TableCell key={thirdDimensionRow.AgentName}>{thirdDimensionRow.AgentName}</TableCell>
                                                <TableCell key={thirdDimensionRow.AlertType} align="right">{thirdDimensionRow.AlertType}</TableCell>
                                                <TableCell key={thirdDimensionRow.RuleName} align="right">{thirdDimensionRow.RuleName}</TableCell>
                                                <TableCell key={thirdDimensionRow.Action} align="right">{thirdDimensionRow.Action}</TableCell>
                                                <TableCell key={thirdDimensionRow.Outcome} align="right">{thirdDimensionRow.Outcome}</TableCell>
                                                <TableCell key={thirdDimensionRow.TeamLeadName} align="right">{thirdDimensionRow.TeamLeadName}</TableCell>
                                                <TableCell key={thirdDimensionRow.Date} align="right">{new Date(thirdDimensionRow.Date).toUTCString()}</TableCell>
                                            </TableRow>
                                            )) : <>
                                            {Object.keys(thirdDimensionData ?? {}).length > 0 ?
                                                thirdDimensionData.data.map(rowInThirDimension => <TableRow key={rowInThirDimension.RowNumber}>
                                                    {
                                                        Array.from(Array(8).keys()).map((number) => <TableCell key={number}>
                                                            <Skeleton className={classes.skeleton} height={30} />
                                                        </TableCell>)
                                                    }
                                                </TableRow>) :
                                                <TableRow key={0}>
                                                    <TableCell>
                                                        <Skeleton className={classes.skeleton} height={30} />
                                                    </TableCell>
                                                </TableRow>
                                            }
                                        </>
                                    }
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow> : null }
        </>: <p>Loading...</p>
    )
}

/* eslint-disable react/forbid-prop-types */
SecondDimensionRow.propTypes = {
    thirdDimensionDataPosition: PropTypes.number.isRequired,
    secondDimensionData: PropTypes.object.isRequired,
    secondDimensionDataPosition: PropTypes.number.isRequired,
    thirdDimensionColumns: PropTypes.array.isRequired,
    row: PropTypes.object.isRequired,
    columns: PropTypes.array.isRequired,
    retrieveDataOnSecondDimension: PropTypes.func.isRequired,
    dataIsLoading: PropTypes.bool.isRequired
}

function FirstDimensionRow(props) {
    const [expandedFirstDimension, setExpandedFirstDimension] = useState(false);
    const { row, secondDimensionDataPosition, columns, handleExpandOnFirstDimension,
        secondDimensionData, secondDimensionColumns, thirdDimensionColumns,
        retrieveDataOnSecondDimension, dataIsLoading, loadingFirstDimensionData } = props;
    const classes = useRowStyles();

    const handleExpandFirstDimension = () => {
        handleExpandOnFirstDimension(secondDimensionDataPosition, row.ClientName, row.ProgramName, row.ProjectName, row.SiteName, !expandedFirstDimension);
        setExpandedFirstDimension(!expandedFirstDimension);
    }

    useEffect(() => {
        setExpandedFirstDimension(false);
    }, [loadingFirstDimensionData]);

    return (
        columns.length && Object.keys(row).length ? <>
            <TableRow className={classes.root}>
                <TableCell>
                    <IconButton disabled={dataIsLoading} aria-label="expand row" size="small" onClick={handleExpandFirstDimension}>
                        {expandedFirstDimension ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                {columns.map((column, index) => {
                            if (index === 0) {
                                return column ? <TableCell component="th" scope="row" key={`${column}${row[column]}`}>{row[column]}</TableCell> : null;
                            }

                            return column ? <TableCell key={`${column}${row[column]}`} align="right">{row[column]}</TableCell> : null;
                        })}
            </TableRow>
            {secondDimensionColumns?.length ? <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                    <Collapse in={expandedFirstDimension} timeout="auto" unmountOnExit>
                        <Box margin={1}>
                            <Typography variant="h6" gutterBottom component="div">
                                Details
                            </Typography>
                            <Table size="small" aria-label="purchases">
                                <TableHead>
                                    <TableRow>
                                        <TableCell />
                                        {secondDimensionColumns.map((column, index) => {
                                            if (index <= 1) {
                                                return column ? <TableCell key={`${column}`}>{column}</TableCell> : null;
                                            }

                                            return column ? <TableCell key={`${column}`} align="right">{column}</TableCell> : null;
                                        })}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {secondDimensionData &&
                                    secondDimensionData.data.map((secondDimensionRow, index) => {
                                            const rowForSecondDimension = { ...secondDimensionRow,
                                                ClientName: row.ClientName,
                                                ProgramName: row.ProgramName,
                                                ProjectName: row.ProjectName,
                                                SiteName: row.SiteName
                                            };

                                            return <SecondDimensionRow key={secondDimensionRow.RuleName} row={rowForSecondDimension}
                                                secondDimensionDataPosition={secondDimensionDataPosition}
                                                thirdDimensionDataPosition={index} columns={secondDimensionColumns}
                                                retrieveDataOnSecondDimension={retrieveDataOnSecondDimension}
                                                secondDimensionData={secondDimensionData}
                                                thirdDimensionColumns={thirdDimensionColumns} dataIsLoading={dataIsLoading} />
                                        }
                                    )}
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow> : null }
        </>: <p>Loading...</p>
    )
}

FirstDimensionRow.propTypes = {
    secondDimensionColumns: PropTypes.array.isRequired,
    secondDimensionData: PropTypes.object,
    secondDimensionDataPosition: PropTypes.number.isRequired,
    thirdDimensionColumns: PropTypes.array.isRequired,
    row: PropTypes.object.isRequired,
    handleExpandOnFirstDimension: PropTypes.func.isRequired,
    columns: PropTypes.array.isRequired,
    retrieveDataOnSecondDimension: PropTypes.func.isRequired,
    dataIsLoading: PropTypes.bool.isRequired,
    loadingFirstDimensionData: PropTypes.bool.isRequired
}

FirstDimensionRow.defaultProps = {
    secondDimensionData: undefined
}

const CollapsibleTable = ({
    gridData, handleExpandOnFirstDimension,
    secondDimensionColumns, thirdDimensionColumns,
    retrieveDataOnSecondDimension, dataIsLoading,
    loadingFirstDimensionData
}) => {
    const classes = useStyles();

    return (
        <TableContainer component={Paper} className={classes.container}>
            <Table aria-label="collapsible table">
                <TableHead>
                    <TableRow>
                        {Object.keys(gridData).length && gridData.columns !== undefined ? 
                            <><TableCell />{gridData.columns.map((column, index) => {
                                    if (index === 0) {
                                        return column ? <TableCell key={column}>{column}</TableCell> : null;
                                    }

                                    return column ? <TableCell key={column} align="right">{column}</TableCell> : null;
                                })} 
                            </> : loadingFirstDimensionData && <>
                                <TableCell>
                                    <Skeleton className={classes.skeleton} height={30} />
                                </TableCell>
                                {Array.from(Array(5).keys()).map((number) =>
                                    <TableCell key={number} align="right">
                                        <Skeleton className={classes.skeleton} height={30} />
                                    </TableCell>
                                )}
                            </> || !loadingFirstDimensionData && <TableCell className={classes.emptyHeaderCell}>No data available</TableCell>
                        }
                    </TableRow>
                </TableHead>
                <TableBody>
                    {
                    Object.keys(gridData).length && gridData.data !== undefined ?
                    gridData.data.map((row, index) => 
                        <FirstDimensionRow
                            key={`${row.ClientName}${row.ProgramName}${row.ProjectName}${row.SiteName}`} row={row} secondDimensionDataPosition={index} columns={gridData.columns} 
                            secondDimensionData={
                                gridData.childDimensionData !== undefined ?
                                gridData.childDimensionData[index] ?? null :
                                undefined
                            }
                            secondDimensionColumns={secondDimensionColumns}
                            handleExpandOnFirstDimension={handleExpandOnFirstDimension}
                            thirdDimensionColumns={thirdDimensionColumns}
                            retrieveDataOnSecondDimension={retrieveDataOnSecondDimension}
                            dataIsLoading={dataIsLoading} loadingFirstDimensionData={loadingFirstDimensionData} />
                    ) : (loadingFirstDimensionData &&
                        Array.from(Array(15).keys()).map((number) => 
                            <TableRow key={number}>
                                {Array.from(Array(6).keys()).map((cellNumber) => 
                                    <TableCell key={cellNumber}>
                                        <Skeleton className={classes.skeleton} height={30} />
                                    </TableCell>
                                )}
                            </TableRow>
                        )) || (!loadingFirstDimensionData && <TableRow><TableCell className={classes.emptyCell} colSpan={6}>Select a client or date range and click Apply to load data</TableCell></TableRow>)
                    }
                </TableBody>
            </Table>
        </TableContainer>
    )
}

CollapsibleTable.propTypes = {
    gridData: PropTypes.object.isRequired,
    handleExpandOnFirstDimension: PropTypes.func.isRequired,
    secondDimensionColumns: PropTypes.array.isRequired,
    thirdDimensionColumns: PropTypes.array.isRequired,
    retrieveDataOnSecondDimension: PropTypes.func.isRequired,
    dataIsLoading: PropTypes.bool.isRequired,
    loadingFirstDimensionData: PropTypes.bool.isRequired
}

export default CollapsibleTable;
